import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Skeleton, SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { projectDataClient } from '../../../lib/connect/clients';
import { Choice, TextField } from '../PastPerformance/Fields';
import { ExternalOutputUpload } from './ExternalOutputUpload';
import { FrozenOutputContext, isIntelligenceOutput, OutputContextFields } from './OutputContext';
import { OutputPresentation } from './OutputPresentation';
import { accessDenied, dateLabel } from './outputPresentationModel';
import { SourceContent } from './SourceContent';
const ids = Testids.captureOutputLifecycle;
export const lifecycleKey = (id)=>[
        'capture-output-lifecycle',
        id
    ];
export function OutputLifecycle({ captureProjectId, templateId, canWrite, active, onDenied, sourceUpdatedAt, zouProjectId = '' }) {
    const location = useLocation();
    const navigate = useNavigate();
    const cache = useQueryClient();
    const params = new URLSearchParams(location.search);
    const versionId = params.get('outputVersion') || '';
    const intelligence = isIntelligenceOutput(templateId);
    const contextKey = `capture-output-context:${captureProjectId}:${templateId}`;
    const [contextDraft, setContextDraft] = useState(()=>{
        try {
            return JSON.parse(sessionStorage.getItem(contextKey) || 'null') || {
                purpose: '',
                audience: '',
                participantIds: []
            };
        } catch  {
            return {
                purpose: '',
                audience: '',
                participantIds: []
            };
        }
    });
    const selectedContext = intelligence && !versionId && contextDraft.purpose.trim() && contextDraft.audience.trim() && contextDraft.participantIds.length ? contextDraft : undefined;
    const comparing = params.get('outputCompare') === '1';
    const query = useQuery({
        queryKey: [
            ...lifecycleKey(captureProjectId),
            templateId,
            versionId,
            selectedContext
        ],
        placeholderData: intelligence ? (previous)=>previous : undefined,
        queryFn: ()=>projectDataClient.reviewCaptureOutput({
                captureProjectId,
                templateId,
                versionId,
                context: selectedContext
            }),
        retry: (count, error)=>!accessDenied(error) && count < 1
    });
    const history = useQuery({
        queryKey: [
            ...lifecycleKey(captureProjectId),
            templateId,
            'versions'
        ],
        queryFn: ()=>projectDataClient.listCaptureOutputVersions({
                captureProjectId,
                templateId
            }),
        retry: (count, error)=>!accessDenied(error) && count < 1
    });
    useEffect(()=>{
        if (sourceUpdatedAt) void cache.invalidateQueries({
            queryKey: lifecycleKey(captureProjectId)
        });
    }, [
        sourceUpdatedAt,
        cache,
        captureProjectId
    ]);
    const wasActive = useRef(active);
    useEffect(()=>{
        if (active && !wasActive.current) void cache.invalidateQueries({
            queryKey: lifecycleKey(captureProjectId)
        });
        wasActive.current = active;
    }, [
        active,
        cache,
        captureProjectId
    ]);
    const [review, setReview] = useState();
    const [decisions, setDecisions] = useState({});
    const [section, setSection] = useState(intelligence ? 'black_hat' : 'management_approach');
    const storageKey = `capture-output-draft:${captureProjectId}:${templateId}:${versionId}:${section}`;
    const draftHead = useRef(sessionStorage.getItem(`${storageKey}:head`) ?? undefined);
    const [wording, setWording] = useState(()=>sessionStorage.getItem(storageKey) || '');
    useEffect(()=>{
        setWording(sessionStorage.getItem(storageKey) || '');
        draftHead.current = sessionStorage.getItem(`${storageKey}:head`) ?? undefined;
    }, [
        storageKey
    ]);
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const [error, setError] = useState('');
    const [draftConflict, setDraftConflict] = useState(false);
    const [message, setMessage] = useState('');
    const data = query.data;
    const current = data?.current;
    const historical = !!versionId && current?.id !== data?.headId;
    const presentationKey = [
        ...lifecycleKey(captureProjectId),
        'presentation',
        current?.id
    ];
    const presentation = useQuery({
        queryKey: presentationKey,
        enabled: !!current,
        queryFn: ()=>projectDataClient.getCaptureOutputPresentation({
                captureProjectId,
                outputVersionId: current?.id || ''
            }),
        retry: (count, error)=>!accessDenied(error) && count < 1
    });
    const [presentationDenied, setPresentationDenied] = useState(false);
    const denied = presentationDenied || accessDenied(query.error) || accessDenied(history.error) || accessDenied(presentation.error);
    useEffect(()=>{
        if (denied) {
            setReview(undefined);
            setWording('');
            sessionStorage.removeItem(storageKey);
            sessionStorage.removeItem(`${storageKey}:head`);
            onDenied();
        }
    }, [
        denied,
        storageKey,
        onDenied
    ]);
    function route(version, compare = false) {
        const next = new URLSearchParams(location.search);
        next.set('tab', 'outputs');
        if (version) next.set('outputVersion', version);
        else next.delete('outputVersion');
        if (compare) next.set('outputCompare', '1');
        else next.delete('outputCompare');
        navigate({
            pathname: location.pathname,
            search: next.toString()
        });
    }
    async function compare() {
        setError('');
        setBusy(true);
        try {
            const result = await query.refetch({
                throwOnError: true
            });
            setReview(result.data);
            setDecisions({});
            route(versionId, true);
        } catch (cause) {
            if (accessDenied(cause)) onDenied();
            else setError('Comparison could not be loaded. Retry Compare; your wording is retained.');
        } finally{
            setBusy(false);
        }
    }
    async function save(operation, artifactId = '') {
        if (inFlight.current || !canWrite || historical || !data) return;
        const basis = operation === 'regenerate' ? review : data;
        if (!basis) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        setMessage('');
        try {
            await projectDataClient.saveCaptureOutputVersion({
                captureProjectId,
                templateId,
                operation,
                artifactId,
                expectedHeadId: operation === 'edit' ? draftHead.current ?? basis.headId : basis.headId,
                reviewedSourceFingerprint: basis.sourceFingerprint,
                context: operation === 'generate' ? basis.prospective?.context : undefined,
                overrides: operation === 'edit' ? [
                    {
                        sectionKey: section,
                        wording
                    }
                ] : [],
                decisions: operation === 'regenerate' ? (basis.current?.overrides ?? []).map((o)=>({
                        sectionKey: o.sectionKey,
                        action: decisions[o.sectionKey]
                    })) : []
            });
            if (operation === 'edit') {
                setDraftConflict(false);
                setWording('');
                sessionStorage.removeItem(storageKey);
                sessionStorage.removeItem(`${storageKey}:head`);
                draftHead.current = undefined;
            }
            setReview(undefined);
            setDecisions({});
            route('');
            setMessage('New immutable output version saved. Prior versions remain in History.');
            await cache.invalidateQueries({
                queryKey: lifecycleKey(captureProjectId)
            });
        } catch (cause) {
            if (accessDenied(cause)) onDenied();
            else setError('Save failed or the output/source changed. Your draft and choices are retained. Refresh comparison before retrying regeneration.');
            if (operation === 'edit' && ConnectError.from(cause).code === Code.Aborted) setDraftConflict(true);
            if (operation === 'external') throw cause;
        } finally{
            setBusy(false);
            inFlight.current = false;
        }
    }
    if (denied) return /*#__PURE__*/ _jsx("div", {
        role: "alert",
        children: "Output access is unavailable. No content is displayed."
    });
    if (query.isPending) return /*#__PURE__*/ _jsxs("output", {
        "data-testid": ids.loading,
        children: [
            "Loading generated outputs…",
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-32 w-full"
            })
        ]
    });
    if (query.isError) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        children: [
            "Generated outputs could not be loaded.",
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": ids.retry,
                onClick: ()=>query.refetch(),
                children: "Retry generated outputs"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": ids.root,
        className: "space-y-4 rounded-md border p-4",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "text-lg font-semibold",
                children: "Generated output · versioned presentation"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Generate from authorized sources. Edit This Output Only saves presentation wording in a new version; Edit Source below opens the owning records."
            }),
            intelligence && !current && /*#__PURE__*/ _jsx(OutputContextFields, {
                captureProjectId: captureProjectId,
                value: contextDraft,
                onChange: (value)=>{
                    setContextDraft(value);
                    sessionStorage.setItem(contextKey, JSON.stringify(value));
                }
            }),
            !current ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "No generated output yet. Partial source material can be generated with its recorded gaps."
                    }),
                    canWrite && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": ids.generate,
                        disabled: busy || query.isFetching || intelligence && !data?.prospective?.context,
                        onClick: ()=>save('generate'),
                        children: busy ? 'Generating…' : templateId === 'black-hat-report-v1' ? 'Generate Black Hat report' : templateId === 'executive-brief-v1' ? 'Generate Executive Brief' : 'Generate output'
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(OutputPresentation, {
                        captureProjectId: captureProjectId,
                        version: current,
                        query: presentation,
                        queryKey: presentationKey,
                        canWrite: canWrite,
                        onDenied: ()=>setPresentationDenied(true)
                    }, current.id),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": ids.current,
                        "data-version-id": current.id,
                        "data-output-id": current.outputId,
                        children: [
                            /*#__PURE__*/ _jsxs("h4", {
                                children: [
                                    "Output version ",
                                    current.version,
                                    " · ",
                                    historical ? 'Historical' : 'Current',
                                    " ·",
                                    ' ',
                                    dateLabel(current.createdAt)
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    current.operation,
                                    " · source basis ",
                                    dateLabel(current.projection?.asOf || '')
                                ]
                            }),
                            data?.changes.length ? /*#__PURE__*/ _jsxs("div", {
                                "data-testid": ids.stale,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "warning",
                                        children: "STALE"
                                    }),
                                    " Newer authorized information:",
                                    ' ',
                                    data.changes.map((c)=>c.title).join('; '),
                                    ". This historical content remains unchanged."
                                ]
                            }) : /*#__PURE__*/ _jsx("p", {
                                children: "Source basis is current at the last check."
                            }),
                            current.externalFile ? /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "warning",
                                        children: "No structured parity"
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "External final file: ",
                                            current.externalFile.filename,
                                            " · artifact version",
                                            ' ',
                                            current.externalFile.artifactVersion
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            current.externalFile.artifactId,
                                            " · SHA-256 ",
                                            current.externalFile.checksumSha256
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "The structured source basis and overrides are retained for regeneration; no claim is made that they match this file."
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(VersionContent, {
                                version: current
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": ids.live,
                        variant: "secondary",
                        onClick: ()=>{
                            setReview(undefined);
                            route('');
                        },
                        children: "Return to current output"
                    }),
                    !historical && canWrite && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3",
                        children: [
                            !current.externalFile && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Choice, {
                                        label: "Output section",
                                        testId: ids.section,
                                        value: section,
                                        onChange: setSection,
                                        options: (current.projection?.sections ?? []).filter((s)=>s.key !== 'output_context').map((s)=>({
                                                value: s.key,
                                                label: s.title
                                            }))
                                    }),
                                    /*#__PURE__*/ _jsx(TextField, {
                                        label: "Edit This Output Only · wording",
                                        testId: ids.wording,
                                        value: wording,
                                        multiline: true,
                                        onChange: (value)=>{
                                            if (draftHead.current === undefined) {
                                                draftHead.current = data?.headId;
                                                sessionStorage.setItem(`${storageKey}:head`, data?.headId || '');
                                            }
                                            setWording(value);
                                            sessionStorage.setItem(storageKey, value);
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": ids.edit,
                                        disabled: busy || draftConflict || !wording.trim(),
                                        onClick: ()=>save('edit'),
                                        children: busy ? 'Saving…' : 'Save output-only wording as new version'
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.compare,
                                variant: "secondary",
                                disabled: busy,
                                onClick: compare,
                                children: "Compare / review regeneration"
                            })
                        ]
                    }),
                    comparing && !review && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Comparison must be refreshed after navigation.",
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                disabled: busy,
                                onClick: compare,
                                children: "Refresh comparison"
                            })
                        ]
                    }),
                    comparing && review && /*#__PURE__*/ _jsxs("section", {
                        "data-testid": ids.comparison,
                        className: "space-y-3 border-t pt-3",
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                children: "Review regeneration · current wording beside prospective source wording"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-1 gap-4 lg:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("h5", {
                                                children: "Current output including overrides"
                                            }),
                                            review.current?.externalFile && /*#__PURE__*/ _jsxs("p", {
                                                children: [
                                                    "External file: ",
                                                    review.current.externalFile.filename,
                                                    " · No structured parity. The following is the retained structured basis, not the external file contents."
                                                ]
                                            }),
                                            review.current && /*#__PURE__*/ _jsx(VersionContent, {
                                                version: review.current
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        "data-testid": ids.prospective,
                                        children: [
                                            /*#__PURE__*/ _jsx("h5", {
                                                children: "Prospective regenerated output · before override decisions"
                                            }),
                                            review.prospective?.sections.map((s)=>/*#__PURE__*/ _jsxs("section", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("h5", {
                                                            children: s.title
                                                        }),
                                                        s.sources.map((source)=>/*#__PURE__*/ _jsx(SourceContent, {
                                                                content: source.contentJson
                                                            }, `${source.id}:${source.version}`))
                                                    ]
                                                }, s.key))
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: "Every presentation override requires a decision. Preserve reapplies its exact wording. Replace explicitly uses the prospective source wording."
                            }),
                            review.current?.overrides.map((o)=>/*#__PURE__*/ _jsxs("div", {
                                    "data-section-key": o.sectionKey,
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                o.sectionKey,
                                                ": ",
                                                o.wording,
                                                " ·",
                                                ' ',
                                                review.changes.some((c)=>c.sectionKey === o.sectionKey) ? 'Source changed; override affected' : 'Source unchanged; override still requires a decision'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Choice, {
                                            label: `Override decision · ${o.sectionKey}`,
                                            testId: ids.decision,
                                            value: decisions[o.sectionKey] || '',
                                            options: [
                                                {
                                                    value: '',
                                                    label: 'Choose explicitly'
                                                },
                                                {
                                                    value: 'preserve',
                                                    label: 'Preserve / reapply wording'
                                                },
                                                {
                                                    value: 'replace',
                                                    label: 'Replace with source wording'
                                                }
                                            ],
                                            onChange: (value)=>setDecisions((previous)=>({
                                                        ...previous,
                                                        [o.sectionKey]: value
                                                    }))
                                        })
                                    ]
                                }, o.sectionKey)),
                            canWrite && !historical && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.regenerate,
                                disabled: busy || (review.current?.overrides ?? []).some((o)=>!decisions[o.sectionKey]),
                                onClick: ()=>save('regenerate'),
                                children: busy ? 'Regenerating…' : 'Confirm reviewed regeneration'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                children: "Timestamped source-change history"
                            }),
                            !data?.history.length && /*#__PURE__*/ _jsx("p", {
                                children: "No material source changes observed for this version."
                            }),
                            data?.history.map((e)=>/*#__PURE__*/ _jsxs("div", {
                                    "data-testid": ids.event,
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: dateLabel(e.observedAt)
                                        }),
                                        e.changes.map((c)=>/*#__PURE__*/ _jsxs("div", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: c.title
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            "Previous:",
                                                            ' ',
                                                            c.previousSources.map((s)=>`${s.kind} ${s.id} @ ${s.version}`).join('; ') || 'No source'
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            "Current:",
                                                            ' ',
                                                            c.currentSources.map((s)=>`${s.kind} ${s.id} @ ${s.version}`).join('; ') || 'No source'
                                                        ]
                                                    })
                                                ]
                                            }, c.sectionKey))
                                    ]
                                }, e.id))
                        ]
                    })
                ]
            }),
            canWrite && !historical && !intelligence && /*#__PURE__*/ _jsx(ExternalOutputUpload, {
                captureProjectId: captureProjectId,
                zouProjectId: zouProjectId,
                templateId: templateId,
                disabled: busy,
                onAttach: (artifactId)=>save('external', artifactId)
            }),
            error && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    error,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": ids.retry,
                        onClick: ()=>{
                            void query.refetch();
                            void history.refetch();
                        },
                        children: "Refresh output"
                    })
                ]
            }),
            draftConflict && /*#__PURE__*/ _jsx(SpecraButton, {
                disabled: busy || query.isFetching,
                onClick: async ()=>{
                    const latest = await query.refetch();
                    if (!latest.data || latest.isError) return;
                    draftHead.current = latest.data.headId;
                    sessionStorage.setItem(`${storageKey}:head`, latest.data.headId);
                    setDraftConflict(false);
                    setError('');
                    setMessage('Latest output loaded. Review it beside your retained wording before saving.');
                },
                children: "Review latest output and reapply retained draft"
            }),
            message && /*#__PURE__*/ _jsx("output", {
                children: message
            }),
            /*#__PURE__*/ _jsxs("section", {
                "data-testid": ids.history,
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        children: "History · immutable generated versions"
                    }),
                    history.isPending ? /*#__PURE__*/ _jsx("p", {
                        children: "Loading version history…"
                    }) : history.isError ? /*#__PURE__*/ _jsxs("div", {
                        role: "alert",
                        children: [
                            "Version history unavailable.",
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>history.refetch(),
                                children: "Retry history"
                            })
                        ]
                    }) : history.data?.versions.map((v)=>/*#__PURE__*/ _jsx("div", {
                            "data-version-id": v.id,
                            children: /*#__PURE__*/ _jsxs(SpecraButton, {
                                "data-testid": ids.versionOpen,
                                variant: "secondary",
                                onClick: ()=>{
                                    setReview(undefined);
                                    route(v.id);
                                },
                                children: [
                                    "Inspect version ",
                                    v.version,
                                    " · ",
                                    v.operation,
                                    " · ",
                                    dateLabel(v.createdAt)
                                ]
                            })
                        }, v.id))
                ]
            })
        ]
    });
}
function VersionContent({ version }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(FrozenOutputContext, {
                context: version.projection?.context
            }),
            version.projection?.sections.filter((s)=>s.key !== 'output_context').map((section)=>{
                const override = version.overrides.find((o)=>o.sectionKey === section.key);
                return /*#__PURE__*/ _jsxs("section", {
                    "data-section-key": section.key,
                    children: [
                        /*#__PURE__*/ _jsx("h5", {
                            children: section.title
                        }),
                        override ? /*#__PURE__*/ _jsxs("p", {
                            className: "whitespace-pre-wrap",
                            children: [
                                "Presentation override: ",
                                override.wording
                            ]
                        }) : section.sources.map((s)=>/*#__PURE__*/ _jsx(SourceContent, {
                                content: s.contentJson
                            }, `${s.id}:${s.version}`)),
                        /*#__PURE__*/ _jsxs("details", {
                            children: [
                                /*#__PURE__*/ _jsx("summary", {
                                    children: "Source versions and recorded gaps"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: section.reason
                                }),
                                section.sources.map((s)=>/*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            s.kind,
                                            " · ",
                                            s.id,
                                            " · version ",
                                            s.version,
                                            " · ",
                                            dateLabel(s.asOf)
                                        ]
                                    }, `${s.id}:${s.version}`))
                            ]
                        })
                    ]
                }, section.key);
            }),
            version.decisions.map((d)=>/*#__PURE__*/ _jsxs("p", {
                    children: [
                        "Regeneration decision:",
                        ' ',
                        version.projection?.sections.find((section)=>section.key === d.sectionKey)?.title || 'Output section',
                        ' ',
                        "·",
                        ' ',
                        d.action === 'preserve' ? 'Keep output-only wording' : version.overrides.some((override)=>override.sectionKey === d.sectionKey) ? 'Use revised output-only wording' : 'Use current source wording'
                    ]
                }, d.sectionKey))
        ]
    });
}
